/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faBriefcase,
  faLayerGroup,
  faDollarSign,
  faBookmark,
  faPaperPlane,
  faEye,
  faStar,
  faClock,
  faCheck,
  faCoins,
  faInfoCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const JobCard = ({ job, onApply, onSave, onViewDetails, isSaved = false, isAlreadyApplied = false }) => {
  const [isApplying, setIsApplying] = useState(false);
  const [isApplied, setIsApplied] = useState(isAlreadyApplied);
  const [coinsAwarded, setCoinsAwarded] = useState(false);
  const [coinsAmount, setCoinsAmount] = useState(0);
  const matchScore = job.match_score ?? job.matchScore ?? 0;
  const matchReasons = Array.isArray(job.match_reasons) && job.match_reasons.length > 0
    ? job.match_reasons
    : (Array.isArray(job.matchReasons) ? job.matchReasons : []);
  
  // Sync isApplied state with isAlreadyApplied prop
  React.useEffect(() => {
    if (isAlreadyApplied) {
      setIsApplied(true);
      console.log('🟢 Job already applied:', job.title || job.jobTitle, 'ID:', job._id || job.id);
    }
  }, [isAlreadyApplied, job]);

  const sanitizeJobTitle = (title) => {
    if (!title) return title;
    return String(title).replace(/^Updated\s*[-:]?\s*/i, "");
  };

  const handleApplyClick = async () => {
    if (isApplied || isApplying || isAlreadyApplied) return;
    
    setIsApplying(true);
    
    if (onApply) {
      try {
        const response = await onApply(job);
        
        // Check if coins were awarded
        if (response && response.coins_awarded) {
          setCoinsAwarded(true);
          setCoinsAmount(response.coins_amount || 0);
          // Hide coin award message after 5 seconds
          setTimeout(() => {
            setCoinsAwarded(false);
          }, 5000);
        }
        
        // After successful application, set to applied permanently
        // The parent will update isAlreadyApplied prop, which will be picked up by useEffect
        setTimeout(() => {
          setIsApplying(false);
          setIsApplied(true); // Keep it applied permanently
        }, 1000);
      } catch (error) {
        console.error('Apply error:', error);
        setIsApplying(false);
        return;
      }
    } else {
      // No onApply handler - just show animation
      setTimeout(() => {
        setIsApplying(false);
        setIsApplied(true);
      }, 1000);
    }
  };

  const formatSalary = (salary) => {
    if (!salary) return 'Competitive';
    
    // If it's already a string, return it
    if (typeof salary === 'string') return salary;
    
    // If it's an object with min/max
    if (typeof salary === 'object' && salary.min !== undefined && salary.max !== undefined) {
      const currency = salary.currency || 'USD';
      const symbol = currency === 'USD' ? '$' : currency;
      return `${symbol}${salary.min} - ${symbol}${salary.max}`;
    }
    
    return 'Competitive';
  };

  const getDisplayCompanyName = () => {
    const hide =
      job.show_company_name === false ||
      job.showCompanyName === false ||
      job.hideCompany === true ||
      job.hideCompanyName === true;
    if (hide) {
      return 'Confidential';
    }
    return job.company_name || job.companyName || job.company || '—';
  };

  const getCompanyInitial = () => {
    const name = getDisplayCompanyName();
    return name && typeof name === 'string'
      ? name.charAt(0).toUpperCase()
      : 'J';
  };

  return (
    <div className="new-job-card job-card-grid">
      <div className="new-job-card-header">
        <div className="new-job-card-main">
          <div className="new-job-card-logo">
            {getCompanyInitial()}
          </div>
          <div className="new-job-card-info">
            <h3 className="new-job-card-title">
              {sanitizeJobTitle(job.job_title || job.title)}
            </h3>
            <div className="new-job-card-company">
              {getDisplayCompanyName()}
            </div>
            <div className="new-job-card-meta">
              <span className="new-job-card-meta-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="fa-icon" /> 
                {job.location || 'Not specified'}
              </span>
              <span className="new-job-card-meta-item">
                <FontAwesomeIcon icon={faBriefcase} className="fa-icon" /> 
                {job.job_type || job.remote_option || 'Full-time'}
              </span>
              <span className="new-job-card-meta-item">
                <FontAwesomeIcon icon={faLayerGroup} className="fa-icon" /> 
                {job.experience_level || job.experience_required || 'Entry Level'}
              </span>
              <span className="new-job-card-meta-item">
                <FontAwesomeIcon icon={faDollarSign} className="fa-icon" /> 
                {formatSalary(job.salary_range || job.salary)}
              </span>
            </div>
          </div>
        </div>
        <div className="new-job-card-actions">
          {isSaved ? (
            <button 
              className="new-job-card-save-btn"
              onClick={() => onSave && onSave(job._id || job.id)}
              title="Delete from saved"
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : (
            <button 
              className={`new-job-card-save-btn ${isSaved ? 'saved' : ''}`}
              onClick={() => onSave && onSave(job._id || job.id)}
              title="Save"
            >
              <FontAwesomeIcon icon={faBookmark} />
            </button>
          )}
        </div>
      </div>
      
      <div className="new-job-card-tags">
        {/* Remote/Hybrid/On-site tag */}
        {(job.remote_option || job.remoteOption) && (
          <span 
            className="new-job-card-tag" 
            style={{ 
              background: (job.remote_option === 'Remote' || job.remoteOption === 'Remote') ? '#dbeafe' : '#fed7aa',
              color: (job.remote_option === 'Remote' || job.remoteOption === 'Remote') ? '#1e40af' : '#c2410c',
              fontWeight: '600',
              border: `1px solid ${(job.remote_option === 'Remote' || job.remoteOption === 'Remote') ? '#93c5fd' : '#fdba74'}`
            }}
          >
            {job.remote_option || job.remoteOption}
          </span>
        )}
        
        {/* Paid tag if salary exists */}
        {(job.salary_range || job.salary) && (
          <span 
            className="new-job-card-tag" 
            style={{ 
              background: '#d1fae5',
              color: '#065f46',
              fontWeight: '600',
              border: '1px solid #86efac'
            }}
          >
            💰 Paid
          </span>
        )}
        
        {matchScore > 0 && (
          <>
            <span 
              className="new-job-card-tag" 
              style={{
                background: matchScore >= 70 ? '#10b981' : matchScore >= 40 ? '#f59e0b' : '#6b7280',
                color: 'white',
                fontWeight: '600'
              }}
            >
              <FontAwesomeIcon icon={faStar} /> {matchScore}% Match
            </span>
            {matchReasons.length > 0 && (
              <span 
                className="new-job-card-tag"
                style={{ background: '#f1f5f9', color: '#1e293b', border: '1px solid #cbd5f5', cursor: 'help' }}
                title={matchReasons.join('\n')}
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Why matched
              </span>
            )}
          </>
        )}
        {job.featured && (
          <span className="new-job-card-tag featured">
            <FontAwesomeIcon icon={faStar} /> Featured
          </span>
        )}
        {(() => {
          // Parse required_skills safely - handle both string and array
          let skills = job.required_skills || job.skills || [];
          if (typeof skills === 'string') {
            skills = skills.split(',').map(s => s.trim()).filter(s => s);
          }
          return Array.isArray(skills) && skills.slice(0, 3).map((skill, skillIdx) => (
            <span key={skillIdx} className="new-job-card-tag">
              {skill}
            </span>
          ));
        })()}
        <span className="new-job-card-tag meta">
          <FontAwesomeIcon icon={faClock} /> 
          {job.posted_date ? new Date(job.posted_date).toLocaleDateString() : 'Recently'}
        </span>
      </div>
      
      <div className="job-card-grid-buttons">
        <button 
          className={`modern-apply-btn ${isApplying ? 'applying' : ''} ${isApplied ? 'applied' : ''}`}
          onClick={handleApplyClick}
          disabled={isApplying || isApplied}
        >
          <span className="apply-btn-icon">
            <FontAwesomeIcon icon={isApplied ? faCheck : faPaperPlane} />
          </span>
          <span className="apply-btn-text">
            {isApplied ? 'Applied!' : isApplying ? 'Applying...' : 'Apply Now'}
          </span>
          {coinsAwarded && (
            <span className="coins-awarded-badge">
              <FontAwesomeIcon icon={faCoins} /> +{coinsAmount} Coins!
            </span>
          )}
        </button>
        <button 
          className="new-job-card-view-btn"
          onClick={() => onViewDetails && onViewDetails(job)}
        >
          <FontAwesomeIcon icon={faEye} /> View Details
        </button>
      </div>
    </div>
  );
};

export default JobCard;
